<template>
  <div>
    <!-- Loan Details (form + summary) - shown when section is 'all' or 'form' -->
    <template v-if="section !== 'tables'">
    <!-- Loan Details: summary-only (labels + values) for waiting_signatures -->
    <div v-if="summaryOnly" class="mb-8">
      <div class="d-flex align-center mb-2">
        <h3 class="font-weight-bold mr-2">{{ $t('finance.loanApplications.loanDetails') }}</h3>
        <v-tooltip top color="#00BD73" max-width="350">
          <template v-slot:activator="{ on, attrs }">
            <v-icon class="ml-1" size="18" style="color: #00BD73; cursor: pointer;" v-bind="attrs" v-on="on">mdi-information</v-icon>
          </template>
          <span>{{ $t('finance.loanApplications.loanDetailsSummary') }}</span>
        </v-tooltip>
      </div>
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <div class="view-block">
            <label class="view-label">{{ $t('finance.loanApplications.totalLoanAmount') }}</label>
            <div class="view-value">{{ loanDetailsSummary.totalLoan }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="view-block">
            <label class="view-label">{{ $t('finance.loanApplications.interestRate') }}</label>
            <div class="view-value">{{ loanDetails.interestRate }}{{ interestRateUnitLabel }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="view-block">
            <label class="view-label">{{ $t('finance.loanApplications.numberOfInstallments') }}</label>
            <div class="view-value">{{ loanDetails.numInstallments }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="view-block">
            <label class="view-label">{{ $t('finance.loanApplications.gracePeriod') }}</label>
            <div class="view-value">{{ loanDetails.gracePeriod }} {{ gracePeriodUnitLabel }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="view-block">
            <label class="view-label">{{ $t('finance.loanApplications.numberOfDisbursements') }}</label>
            <div class="view-value">{{ loanDetails.numDisbursements }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="view-block">
            <label class="view-label">{{ $t('finance.loanApplications.startDate') }}</label>
            <div class="view-value">{{ loanDetails.startDate.split("T")[0] }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="view-block">
            <label class="view-label">{{ $t('finance.loanApplications.totalInterestApprox') }}</label>
            <div class="view-value">{{ loanDetailsSummary.totalInterest }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="view-block">
            <label class="view-label">{{ $t('finance.loanApplications.totalRepayment') }}</label>
            <div class="view-value">{{ loanDetailsSummary.totalRepayment }}</div>
          </div>
        </v-col>
      </v-row>
    </div>
    <!-- Loan Details: full form (editable or readonly) -->
    <div v-else class="mb-8">
        <div class="d-flex align-center mb-2">
          <h3 class="font-weight-bold mr-2">{{ $t('finance.loanApplications.loanDetails') }}</h3>
          <v-tooltip top color="#00BD73" max-width="350">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="ml-1" size="18" style="color: #00BD73; cursor: pointer;" v-bind="attrs" v-on="on">mdi-information</v-icon>
            </template>
            <span>{{ $t('finance.loanApplications.selectNoIfYouHaveAlreadySignedTheContractWithTheApplicantFarmerSelectYesIfYouWantTheApplicantFarmerToSignTheLoanContractOnHisDimitraMobileApp') }}</span>
          </v-tooltip>
        </div>
        <p class="finalize-contract-desc mb-4">
          {{ $t('finance.loanApplications.selectNoIfYouHaveAlreadySignedTheContractWithTheApplicantFarmerSelectYesIfYouWantTheApplicantFarmerToSignTheLoanContractOnHisDimitraMobileApp') }}
        </p>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <div class="d-flex justify-space-between align-center mb-1">
              <label class="view-label mb-0">{{ $t('finance.loanApplications.loanAmount') }}</label>
              <span class="view-label mb-0">{{ $t('finance.loanApplications.srd') }}</span>
            </div>
            <v-text-field v-model="loanDetails.loanAmount" outlined dense hide-details :readonly="readonly" :disabled="readonly" />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <label class="view-label mb-1">{{ $t('finance.loanApplications.interestRate') }}</label>
            <v-row no-gutters class="d-flex unit-combined">
              <v-col class="unit-input-col">
                <v-text-field v-model="loanDetails.interestRate" outlined dense hide-details class="rounded-r-0" :readonly="readonly" :disabled="readonly" />
              </v-col>
              <v-col class="unit-select-col flex-grow-0">
                <v-select
                  v-model="loanDetails.interestRateUnit"
                  :items="interestRateUnitOptions"
                  item-text="label"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  class="unit-select rounded-l-0"
                  :readonly="readonly"
                  :disabled="readonly"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <label class="view-label mb-1">{{ $t('finance.loanApplications.numberOfDisbursements') }}</label>
            <v-text-field v-model="loanDetails.numDisbursements" outlined dense hide-details :readonly="readonly" :disabled="readonly" />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <label class="view-label mb-1">{{ $t('finance.loanApplications.numberOfInstallments') }}</label>
            <v-text-field v-model="loanDetails.numInstallments" outlined dense hide-details :readonly="readonly" :disabled="readonly" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <label class="view-label mb-1">{{ $t('finance.loanApplications.gracePeriod') }}</label>
            <v-row no-gutters class="d-flex unit-combined">
              <v-col class="unit-input-col">
                <v-text-field v-model="loanDetails.gracePeriod" outlined dense hide-details class="rounded-r-0" :readonly="readonly" :disabled="readonly" />
              </v-col>
              <v-col class="unit-select-col flex-grow-0">
                <v-select
                  v-model="loanDetails.gracePeriodUnit"
                  :items="gracePeriodUnitOptions"
                  item-text="label"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  class="unit-select rounded-l-0"
                  :readonly="readonly"
                  :disabled="readonly"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <label class="view-label mb-1">{{ $t('finance.loanApplications.gracePeriodType') }}</label>
            <v-select
              v-model="loanDetails.gracePeriodType"
              :items="gracePeriodTypeOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              :readonly="readonly"
              :disabled="readonly"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <label class="view-label mb-1">{{ $t('finance.loanApplications.startDate') }}</label>
            <!-- <v-text-field v-model="loanDetails.startDate" outlined dense hide-details append-icon="mdi-calendar" readonly :disabled="readonly" /> -->
            <template>
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="loanDetails.startDate"
                    placeholder="Select Date"
                    append-icon="mdi-calendar"
                    outlined
                    dense
                    readonly
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    style="height: 40px;"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="loanDetails.startDate"
                  @input="dateMenu = false"
                ></v-date-picker>
              </v-menu>
            </template>
          </v-col>
          <v-col cols="12" sm="6" md="3"></v-col>
        </v-row>
        <div class="loan-summary-box mt-4 pa-4">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <span class="finalize-contract-desc d-block mb-1">{{ $t('finance.loanApplications.totalLoanAmount') }}</span>
              <div class="finalize-contract-subheading">{{ loanDetailsSummary.totalLoan }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <span class="finalize-contract-desc d-block mb-1">{{ $t('finance.loanApplications.totalInterestApprox') }}</span>
              <div class="finalize-contract-subheading">{{ loanDetailsSummary.totalInterest }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <span class="finalize-contract-desc d-block mb-1">{{ $t('finance.loanApplications.totalRepayment') }}</span>
              <div class="finalize-contract-subheading">{{ loanDetailsSummary.totalRepayment }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3"></v-col>
          </v-row>
        </div>
      </div>

      <!-- Loan Disbursement Plan & Loan Repayment Plan (hidden when summaryOnly, e.g. waiting_signatures – tables shown instead) -->
      <v-row v-if="!summaryOnly">
        <v-col cols="12" md="6">
          <div class="mb-4">
            <div class="d-flex align-center mb-2">
              <h3 class="font-weight-bold mr-2">{{ $t('finance.loanApplications.loanDisbursementPlan') }}</h3>
              <v-tooltip top color="#00BD73" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon class="ml-1" size="18" style="color: #00BD73; cursor: pointer;" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.loanApplications.selectNoIfYouHaveAlreadySignedTheContractWithTheApplicantFarmerSelectYesIfYouWantTheApplicantFarmerToSignTheLoanContractOnHisDimitraMobileApp') }}</span>
              </v-tooltip>
            </div>
            <p class="finalize-contract-desc mb-3">{{ $t('finance.loanApplications.selectNoIfYouHaveAlreadySignedTheContractWithTheApplicantFarmerSelectYesIfYouWantTheApplicantFarmerToSignTheLoanContractOnHisDimitraMobileApp') }}</p>
            <v-btn color="primary" class="my-4 plan-generate-btn" outlined @click="downloadTemplate('disbursement')">
              {{ $t('DOWNLOAD TEMPLATE') }}

              <v-icon>
                mdi-download
              </v-icon>
            </v-btn>
            
            <div class="plan-section-content">
              <div
                :class="['dropZone dropZone--compact', { 'dropZone-over': !readonly && disbursementDragOver, 'dropZone--readonly': readonly }]"
                @dragenter="!readonly && (disbursementDragOver = true)"
                @dragleave="!readonly && (disbursementDragOver = false)"
                @drop.prevent="readonly ? null : onDisbursementDrop($event)"
              >
                <div class="dropZone-info d-flex justify-center align-center" @dragover.prevent @click="!readonly && triggerDisbursementFileInput()">
                  <v-icon size="40" color="#0057BD">mdi-tray-arrow-up</v-icon>
                  <div class="dropZone-upload-limit-info ml-3">
                    <h2 class="blue-color underline mb-1">{{ $t('Upload the Disbursement Plan XLSX') }}</h2>
                    <p class="gray-color ma-0">{{ $t('finance.loanApplications.orDragAndDropHere') }}</p>
                  </div>
                </div>
                <input ref="disbursementFileInput" type="file" accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.pdf,application/pdf" @change="onDisbursementFileSelected" />
              </div>
              <div v-if="disbursementFile" class="d-flex align-start mt-3">
                <div class="attachment-card contract-uploaded-card">
                  <div class="attachment-icon">
                    <span class="attachment-icon-badge">{{ disbursementFileExt }}</span>
                    <v-icon small color="#135B00" class="attachment-download" @click.stop="downloadDisbursementFile">mdi-download</v-icon>
                    <v-icon v-if="!readonly" small color="#135B00" class="attachment-delete" @click.stop="removeDisbursementFile">mdi-delete</v-icon>
                  </div>
                  <div class="attachment-label">{{ disbursementFile.name }}</div>
                </div>
              </div>
              <v-btn v-if="!readonly" color="primary" class="mt-4 plan-generate-btn" depressed @click="$emit('generate-disbursement', disbursementFile)">
                {{ $t('finance.loanApplications.generatePaymentSchedule') }}
              </v-btn>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="mb-4">
            <div class="d-flex align-center mb-2">
              <h3 class="font-weight-bold mr-2">{{ $t('finance.loanApplications.loanRepaymentPlan') }}</h3>
              <v-tooltip top color="#00BD73" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon class="ml-1" size="18" style="color: #00BD73; cursor: pointer;" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.loanApplications.selectNoIfYouHaveAlreadySignedTheContractWithTheApplicantFarmerSelectYesIfYouWantTheApplicantFarmerToSignTheLoanContractOnHisDimitraMobileApp') }}</span>
              </v-tooltip>
            </div>
            <p class="finalize-contract-desc mb-3">{{ $t('finance.loanApplications.selectNoIfYouHaveAlreadySignedTheContractWithTheApplicantFarmerSelectYesIfYouWantTheApplicantFarmerToSignTheLoanContractOnHisDimitraMobileApp') }}</p>
            <v-btn color="primary" class="my-4 plan-generate-btn" outlined @click="downloadTemplate('repayment')">
              {{ $t('DOWNLOAD TEMPLATE') }}

              <template v-slot:append>
                <v-icon>
                  mdi-download
                </v-icon>
              </template>
            </v-btn>
            <div class="plan-section-content">
              <div
                :class="['dropZone dropZone--compact', { 'dropZone-over': !readonly && repaymentDragOver, 'dropZone--readonly': readonly }]"
                @dragenter="!readonly && (repaymentDragOver = true)"
                @dragleave="!readonly && (repaymentDragOver = false)"
                @drop.prevent="readonly ? null : onRepaymentDrop($event)"
              >
                <div class="dropZone-info d-flex justify-center align-center" @dragover.prevent @click="!readonly && triggerRepaymentFileInput()">
                  <v-icon size="40" color="#0057BD">mdi-tray-arrow-up</v-icon>
                  <div class="dropZone-upload-limit-info ml-3">
                    <h2 class="blue-color underline mb-1">{{ $t('Upload the Repayment Plan XLSX') }}</h2>
                    <p class="gray-color ma-0">{{ $t('finance.loanApplications.orDragAndDropHere') }}</p>
                  </div>
                </div>
                <input ref="repaymentFileInput" type="file" accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.pdf,application/pdf" @change="onRepaymentFileSelected" />
              </div>
              <div v-if="repaymentFile" class="d-flex align-start mt-3">
                <div class="attachment-card contract-uploaded-card">
                  <div class="attachment-icon">
                    <span class="attachment-icon-badge">{{ repaymentFileExt }}</span>
                    <v-icon small color="#135B00" class="attachment-download" @click.stop="downloadRepaymentFile">mdi-download</v-icon>
                    <v-icon v-if="!readonly" small color="#135B00" class="attachment-delete" @click.stop="removeRepaymentFile">mdi-delete</v-icon>
                  </div>
                  <div class="attachment-label">{{ repaymentFile.name }}</div>
                </div>
              </div>
              <v-btn color="primary" class="mt-4 plan-generate-btn" depressed @click="$emit('generate-repayment', repaymentFile)">
                {{ $t('finance.loanApplications.generatePaymentSchedule') }}
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </template>

      <!-- Loan Disbursement Table (Bank -> Farmer) - shown when section is 'all' or 'tables' -->
      <template v-if="section !== 'form'">
      <!-- Loan Disbursement Table (Bank -> Farmer) - same design as Activity Log -->
      <div v-if="disbursementScheduleGenerated" class="mt-8 mb-8 schedule-section">
        <div class="d-flex align-center justify-space-between flex-wrap mb-2">
          <div class="d-flex align-center mb-2">
            <h3 class="font-weight-bold mr-2 schedule-section-title">
              {{ $t('finance.loanApplications.loanDisbursementBankFarmer') }}
            </h3>
            <v-tooltip top color="#00BD73" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ml-1" size="18" style="color: #00BD73; cursor: pointer;" v-bind="attrs" v-on="on">mdi-information</v-icon>
              </template>
              <span>{{ $t('finance.loanApplications.disbursementScheduleFromBankToFarmer') }}</span>
            </v-tooltip>
          </div>
          <span class="schedule-section-title">{{ $t('finance.loanApplications.totalInstallments') }}: {{ filteredDisbursementSchedule.length }}</span>
        </div>

        <v-row class="mb-4 d-flex align-center">
          <v-col cols="12" md="4" class="schedule-filter-field">
            <label class="view-label mb-1">{{ $t('finance.loanApplications.search') }}</label>
            <v-text-field
              :value="disbursementSearch"
              @input="$emit('update:disbursementSearch', $event)"
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              :placeholder="$t('finance.loanApplications.search')"
            />
          </v-col>
          <v-col cols="12" md="4" class="schedule-filter-field">
            <label class="view-label mb-1">{{ $t('finance.loanApplications.status') }}</label>
            <v-select
              :value="disbursementStatusFilter"
              @input="$emit('update:disbursementStatusFilter', $event)"
              :items="scheduleStatusOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              :placeholder="$t('finance.loanApplications.select')"
              clearable
            />
          </v-col>
        </v-row>

        <v-data-table
          :headers="disbursementTableHeaders"
          :items="filteredDisbursementSchedule"
          class="activity-log-table"
          hide-default-footer
        >
          <template v-slot:item.status="{ item }">
            <v-btn small rounded depressed :class="['status-pill-fixed text-capitalize', getScheduleStatusClass(item.status)]">
              {{ getScheduleStatusText(item.status) }}
            </v-btn>
          </template>
          <template v-slot:item.dueDate="{ item }">
            {{ item.dueDate.split("T")[0] }}
          </template>
          <template v-slot:item.transactionId="{ item }">
            <template v-if="!item.transactionId">-</template>
            <template v-else>{{ item.transactionId }}<br>{{ item.paymentDate?.split('T')[0] }}</template>
          </template>
          <template v-slot:item.actions="{ item, index }">
            <v-btn x-small fab color="primary" outlined class="ml-2" @click.stop="openPaymentDetailsDialog(item, index)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </div>

      <!-- Loan Repayment Table (Farmer -> Bank) - same design as Activity Log -->
      <div v-if="repaymentScheduleGenerated" class="mt-8 mb-8 schedule-section">
        <div class="d-flex align-center justify-space-between flex-wrap mb-2">
          <div class="d-flex align-center mb-2">
            <h3 class="font-weight-bold mr-2 schedule-section-title">
              {{ $t('finance.loanApplications.loanRepaymentFarmerBank') }}
            </h3>
            <v-tooltip top color="#00BD73" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ml-1" size="18" style="color: #00BD73; cursor: pointer;" v-bind="attrs" v-on="on">mdi-information</v-icon>
              </template>
              <span>{{ $t('finance.loanApplications.repaymentScheduleFromFarmerToBank') }}</span>
            </v-tooltip>
          </div>
          <span class="schedule-section-title">{{ $t('finance.loanApplications.totalInstallments') }}: {{ filteredRepaymentSchedule.length }}</span>
        </div>

        <v-row class="mb-4 d-flex align-center">
          <v-col cols="12" md="4" class="schedule-filter-field">
            <label class="view-label mb-1">{{ $t('finance.loanApplications.search') }}</label>
            <v-text-field
              :value="repaymentSearch"
              @input="$emit('update:repaymentSearch', $event)"
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              :placeholder="$t('finance.loanApplications.search')"
            />
          </v-col>
          <v-col cols="12" md="4" class="schedule-filter-field">
            <label class="view-label mb-1">{{ $t('finance.loanApplications.status') }}</label>
            <v-select
              :value="repaymentStatusFilter"
              @input="$emit('update:repaymentStatusFilter', $event)"
              :items="scheduleStatusOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              :placeholder="$t('finance.loanApplications.select')"
              clearable
            />
          </v-col>
          <v-col cols="12" md="4" class="schedule-pagination-col d-flex align-center justify-end flex-wrap">
            <span class="py-1 title mr-2">{{ (repaymentSchedulePage - 1) * repaymentSchedulePerPage + 1 }}-{{ Math.min(repaymentSchedulePage * repaymentSchedulePerPage, filteredRepaymentSchedule.length) }} {{ $t('finance.loanApplications.of') }} {{ filteredRepaymentSchedule.length }}</span>
            <v-btn class="mx-1" fab small outlined color="primary" :disabled="repaymentSchedulePage <= 1" @click="$emit('update:repaymentSchedulePage', Math.max(1, repaymentSchedulePage - 1))">
              <v-icon dark>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn class="mx-1" fab small outlined color="primary" :disabled="repaymentSchedulePage >= repaymentScheduleTotalPages" @click="$emit('update:repaymentSchedulePage', Math.min(repaymentScheduleTotalPages, repaymentSchedulePage + 1))">
              <v-icon dark>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn class="mx-1" fab small outlined color="primary">
              <v-icon dark>mdi-cog-outline</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-data-table
          :headers="repaymentTableHeaders"
          :items="paginatedRepaymentSchedule"
          class="activity-log-table"
          hide-default-footer
        >
          <template v-slot:item.principalInterest="{ item }">{{ item.interestAmount }}<br>{{ item.principalAmount }}</template>
          <template v-slot:item.status="{ item }">
            <v-btn small rounded depressed :class="['status-pill-fixed text-capitalize', getScheduleStatusClass(item.status)]">
              {{ getScheduleStatusText(item.status) }}
            </v-btn>
          </template>
          <template v-slot:item.dueDate="{ item }">
            {{ item.dueDate.split("T")[0] }}
          </template>
          <template v-slot:item.actions="{ item, index }">
            <v-btn x-small fab color="primary" outlined class="ml-2" @click.stop="openInstallmentDetailsDialog(item, index)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </div>
      </template>

    <!-- Payment Details dialog (disbursement edit) -->
    <v-dialog
      v-model="showPaymentDetailsDialog"
      persistent
      max-width="560"
      content-class="payment-details-dialog"
    >
      <v-card>
        <v-toolbar dense flat color="primary" class="white--text">
          <span class="payment-details-dialog-title">{{ $t('finance.loanApplications.paymentDetails') }}</span>
          <v-spacer />
          <v-btn icon dark @click="closePaymentDetailsDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-6">
          <h3 class="payment-details-heading mb-4">{{ paymentDetailsForm.payment }}</h3>
          <v-row class="mb-4">
            <v-col cols="12" sm="6">
              <div class="d-flex justify-space-between align-center mb-1">
                <label class="view-label mb-0">{{ $t('finance.loanApplications.amount') }}</label>
                <span class="view-label mb-0">{{ $t('finance.loanApplications.srd') }}</span>
              </div>
              <v-text-field
                v-model="paymentDetailsForm.amount"
                outlined
                dense
                hide-details
                :readonly="paymentDetailsForm.readonly"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="view-label mb-1 d-block">{{ $t('finance.loanApplications.date') }}</label>
              <!-- <v-text-field
                v-model="paymentDetailsForm.date"
                outlined
                dense
                hide-details
                append-icon="mdi-calendar"
                readonly
              /> -->
              <template>
                <v-menu
                  v-model="paymentDetailsForm.dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="paymentDetailsForm.paymentDate"
                      placeholder="Select Date"
                      append-icon="mdi-calendar"
                      outlined
                      dense
                      readonly
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                      style="height: 40px;"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="paymentDetailsForm.paymentDate"
                    @input="paymentDetailsForm.dateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </template>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="view-label mb-1 d-block">{{ $t('finance.loanApplications.status') }}</label>
              <v-select
                v-model="paymentDetailsForm.status"
                :items="paymentDetailsStatusOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
              />
            </v-col> 
          </v-row>
          <template v-if="paymentDetailsForm.id != null">
            <hr style="background-color: #e2e2e2; height: 2px; border: none;">
            <v-row class="mt-4">
              <v-col cols="12" sm="6">
                <div class="d-flex justify-space-between align-center mb-1">
                  <label class="view-label mb-0">Payment Method</label>
                </div>
                <v-text-field
                  v-model="paymentDetailsForm.paymentMethod"
                  outlined
                  dense
                  placeholder="Enter Here"
                  hide-details
                  :readonly="paymentDetailsForm.readonly"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <div class="d-flex justify-space-between align-center mb-1">
                  <label class="view-label mb-0">Transaction ID</label>
                </div>
                <v-text-field
                  v-model="paymentDetailsForm.transactionId"
                  outlined
                  dense
                  placeholder="Enter Here"
                  hide-details
                  :readonly="paymentDetailsForm.readonly"
                />
              </v-col>
              <v-col cols="12">
                <div class="mb-4">
                  <div class="font-weight-bold mb-2 finalize-contract-subheading">Transaction Reciept</div>
                  <div v-if="!paymentDetailsForm.readonly"
                    :class="['dropZone', { 'dropZone-over': paymentDetailsForm.dragOver }]"
                    @dragenter="paymentDetailsForm.dragOver = true"
                    @dragleave="paymentDetailsForm.dragOver = false"
                    @drop.prevent="onRecieptDrop($event, 'payment')"
                  >
                    <div
                      class="dropZone-info d-flex justify-center align-center"
                      @dragover.prevent
                      @click="triggerPaymentRecieptFileInput()"
                    >
                      <v-icon size="40" color="#0057BD">mdi-tray-arrow-up</v-icon>
                      <div class="dropZone-upload-limit-info ml-3">
                        <h2 class="blue-color underline mb-1">
                          Select transaction reciept file to upload
                        </h2>
                        <p class="gray-color ma-0">{{ $t('finance.loanApplications.orDragAndDropHere') }}</p>
                      </div>
                    </div>
                    <input
                      ref="paymentRecieptFileInput"
                      type="file"
                      accept=".pdf,application/pdf"
                      @change="onRecieptSelected($event, 'payment')"
                    />
                  </div>
                  <div v-if="paymentDetailsForm.transactionReceipt && !paymentDetailsForm.readonly" class="d-flex align-start mt-3">
                    <div class="attachment-card contract-uploaded-card">
                      <div class="attachment-icon">
                        <span class="attachment-icon-badge">PDF</span>
                        <v-icon small color="#135B00" class="attachment-download" @click.stop="downloadReceipt(paymentDetailsForm.transactionReceipt)">
                          mdi-download
                        </v-icon>
                        <v-icon small color="#135B00" class="attachment-delete" @click.stop="removeReciept(paymentDetailsForm)">
                          mdi-delete
                        </v-icon>
                      </div>
                      <div class="attachment-label">{{ paymentDetailsForm.transactionReceipt.name }}</div>
                    </div>
                  </div>
                  <div v-if="paymentDetailsForm.readonly && paymentDetailsForm.transactionReceipt" class="d-flex align-start mt-3">
                    <div class="attachment-card contract-uploaded-card">
                      <div class="attachment-icon">
                        <span class="attachment-icon-badge">PDF</span>
                        <v-icon small color="#135B00" class="attachment-download" @click.stop="downloadReceipt(paymentDetailsForm.transactionReceipt)">
                          mdi-download
                        </v-icon>
                      </div>
                      <div class="attachment-label">{{ getFilenameFromURL(paymentDetailsForm.transactionReceipt) }}</div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0 justify-end">
          <v-btn color="primary" class="payment-details-close-btn" outlined @click="closePaymentDetailsDialog">
            {{ $t('finance.loanApplications.closeUpper') }}
          </v-btn>
          <v-btn v-if="!paymentDetailsForm.readonly" color="primary" class="installment-details-close-btn" depressed
              @click="submitPaymentUpdate">
              Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Installment Details dialog (repayment edit) -->
    <v-dialog
      v-model="showInstallmentDetailsDialog"
      persistent
      max-width="560"
      content-class="installment-details-dialog"
    >
      <v-card>
        <v-toolbar dense flat color="primary" class="white--text">
          <span class="installment-details-dialog-title">{{ $t('finance.loanApplications.installmentDetails') }}</span>
          <v-spacer />
          <v-btn icon dark @click="closeInstallmentDetailsDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-6">
          <h3 class="installment-details-heading mb-4">{{ installmentDetailsForm.installment }}</h3>
          <v-row>
            <v-col cols="12" sm="6">
              <div class="d-flex justify-space-between align-center mb-1">
                <label class="view-label mb-0">{{ $t('finance.loanApplications.amount') }}</label>
                <span class="view-label mb-0">{{ $t('finance.loanApplications.srd') }}</span>
              </div>
              <v-text-field
                v-model="installmentDetailsForm.amount"
                outlined
                dense
                hide-details
                :placeholder="$t('finance.loanApplications.enterHere')"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <label class="view-label mb-1 d-block">{{ $t('finance.loanApplications.dueDate') }}</label>
              <!-- <v-text-field
                v-model="installmentDetailsForm.dueDate"
                outlined
                dense
                hide-details
                append-icon="mdi-calendar"
                readonly
              /> -->
              <template>
                <v-menu
                  v-model="installmentDetailsForm.dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="installmentDetailsForm.paymentDate"
                      placeholder="Select Date"
                      append-icon="mdi-calendar"
                      outlined
                      dense
                      readonly
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                      style="height: 40px;"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="installmentDetailsForm.paymentDate"
                    @input="installmentDetailsForm.dateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </template>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="view-label mb-1 d-block">{{ $t('finance.loanApplications.status') }}</label>
              <v-select
                v-model="installmentDetailsForm.status"
                :items="paymentDetailsStatusOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0 justify-end">
          <v-btn color="primary" class="installment-details-close-btn" outlined @click="closeInstallmentDetailsDialog">
            {{ $t('finance.loanApplications.closeUpper') }}
          </v-btn>
          <v-btn color="primary" class="installment-details-close-btn" depressed
              @click="closeInstallmentDetailsDialog">
              Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FinanceServiceV2 from '@/_services/FinanceServiceV2';

export default {
  name: 'LoanDetails',
  props: {
    /** When true, form and tables are read-only (e.g. non-bank user viewing sent_to_bank or waiting_signatures) */
    readonly: {
      type: Boolean,
      default: false,
    },
    /** When true (e.g. waiting_signatures), show only labels and values, no inputs/upload/generate */
    summaryOnly: {
      type: Boolean,
      default: false,
    },
    /** 'form' = loan details form + plans only, 'tables' = schedule tables only, 'all' = everything */
    section: {
      type: String,
      default: 'all',
      validator: (v) => !v || ['form', 'tables', 'all'].includes(v),
    },
    loanDetails: { type: Object, required: true },
    loanDetailsSummary: { type: Object, required: true },
    interestRateUnitOptions: { type: Array, default: () => [] },
    gracePeriodUnitOptions: { type: Array, default: () => [] },
    gracePeriodTypeOptions: { type: Array, default: () => [] },
    disbursementFile: { type: Object, default: null },
    repaymentFile: { type: Object, default: null },
    disbursementFileExt: { type: String, default: 'XLS' },
    repaymentFileExt: { type: String, default: 'XLS' },
    disbursementScheduleGenerated: { type: Boolean, default: false },
    repaymentScheduleGenerated: { type: Boolean, default: false },
    filteredDisbursementSchedule: { type: Array, default: () => [] },
    filteredRepaymentSchedule: { type: Array, default: () => [] },
    paginatedRepaymentSchedule: { type: Array, default: () => [] },
    repaymentScheduleTotalPages: { type: Number, default: 1 },
    disbursementSearch: { type: String, default: '' },
    disbursementStatusFilter: { type: [String, null], default: null },
    repaymentSearch: { type: String, default: '' },
    repaymentStatusFilter: { type: [String, null], default: null },
    repaymentSchedulePage: { type: Number, default: 1 },
    repaymentSchedulePerPage: { type: Number, default: 12 },
    scheduleStatusOptions: { type: Array, default: () => [] },
    disbursementTableHeaders: { type: Array, default: () => [] },
    repaymentTableHeaders: { type: Array, default: () => [] },
  },
  data() {
    return {
      disbursementDragOver: false,
      repaymentDragOver: false,
      showPaymentDetailsDialog: false,
      dateMenu: false,
      paymentDetailsForm: {
        readonly: false,
        index: null,
        id: null,
        payment: '',
        amount: '',
        paymentDate: '',
        status: 'upcoming',
        dateMenu: false,
        dragOver: false,
        paymentMethod: null,
        transactionId: null,
        transactionReceipt: null
      },
      showInstallmentDetailsDialog: false,
      installmentDetailsForm: {
        readonly: false,
        index: null,
        id: null,
        installment: '',
        amount: '',
        paymentDate: '',
        status: 'upcoming',
        dateMenu: false,
        dragOver: false,
        paymentMethod: null,
        transactionId: null,
        transactionReceipt: null
      },
    };
  },
  computed: {
    paymentDetailsStatusOptions() {
      return [
        { text: this.$t('finance.loanApplications.pending'), value: 'pending' },
        { text: this.$t('finance.loanApplications.paid'), value: 'paid' },
      ];
    },
    interestRateUnitLabel() {
      const opt = (this.interestRateUnitOptions || []).find((o) => o.value === this.loanDetails.interestRateUnit);
      return opt ? opt.label : this.loanDetails.interestRateUnit || '';
    },
    gracePeriodUnitLabel() {
      const opt = (this.gracePeriodUnitOptions || []).find((o) => o.value === this.loanDetails.gracePeriodUnit);
      return opt ? opt.label : this.loanDetails.gracePeriodUnit || '';
    },
  },
  methods: {
    async submitPaymentUpdate() {
      console.log("Here")
      try {
        // Application has not been approved yet
        if (!this.paymentDetailsForm.id) {
          if (this.paymentDetailsForm.status === 'paid') {
            this.$notify({
              title: 'Invalid Data',
              text: "Cannot make disbursement paid before approving contract"
            })
          } else {
            // Item has not been uploaded
            this.filteredDisbursementSchedule[this.paymentDetailsForm.index].disbursedAmount = this.paymentDetailsForm.amount;
            this.filteredDisbursementSchedule[this.paymentDetailsForm.index].dueDate = this.paymentDetailsForm.paymentDate;
          }
        } else {
          const formData = new FormData();

          formData.append('transactionId', this.paymentDetailsForm.transactionId || '');
          formData.append('paymentMethod', this.paymentDetailsForm.paymentMethod || '');
          formData.append('paymentDate', this.paymentDetailsForm.paymentDate || '');
          formData.append('status', this.paymentDetailsForm.status);
          formData.append('disbursedAmount', this.paymentDetailsForm.amount || '');
          if (this.paymentDetailsForm.transactionReceipt) {
            formData.append('transactionReceipt', this.paymentDetailsForm.transactionReceipt);
          }

          const response = await FinanceServiceV2.updateDisbursementPaymentDetails(this.paymentDetailsForm.id, formData);

          if (!response.success) throw new Error();

          window.location.reload();
        }

        this.showPaymentDetailsDialog = false;
      } catch (e) {
        this.$notify({
          title: this.$t('finance.loanApplications.error'),
          text: e.message,
          type: 'error'
        });

        throw new Error("Failed to save loan disbursement details");
      }
    },
    openPaymentDetailsDialog(item, index) {
      if (!item) return;
      const amountStr = (item.disbursedAmount || '').replace(/\s*SRD\s*/i, '').trim() || '';

      this.paymentDetailsForm = {
        readonly: item.status === 'paid',
        index,
        id: item.id,
        payment: `Payment ${item.paymentLabel}` || '',
        amount: amountStr,
        paymentDate: item.paymentDate?.split('T')[0] || '',
        status: item.status === 'upcoming' ? 'pending' : item.status === 'completed' ? 'paid' : (item.status || 'pending'),
        dateMenu: false,
        dragOver: false,
        paymentMethod: item.paymentMethod,
        transactionId: item.transactionId,
        transactionReceipt: item.transactionReceipt
      };
      console.log(item)
      this.showPaymentDetailsDialog = true;
    },
    closePaymentDetailsDialog() {
      this.paymentDetailsForm = {
        readonly: false,
        index: null,
        id: null,
        payment: '',
        amount: '',
        paymentDate: '',
        status: 'pending',
        dateMenu: false,
        dragOver: false,
        paymentMethod: null,
        transactionId: null,
        transactionReceipt: null
      };
      this.showPaymentDetailsDialog = false;
    },
    openInstallmentDetailsDialog(item, index) {
      if (!item) return;
      const amountStr = (item.totalPayment || item.principalInterest || '').replace(/\s*SRD\s*/i, '').trim() || '';
      this.installmentDetailsForm = {
        index,
        id: item.id,
        installment: item.installment || '',
        amount: amountStr,
        paymentDate: item.paymentDate || '',
        status: item.status === 'upcoming' ? 'pending' : item.status === 'completed' ? 'paid' : (item.status || 'pending'),

      };
      this.showInstallmentDetailsDialog = true;
    },
    closeInstallmentDetailsDialog() {
      this.installmentDetailsForm = {
        readonly: false,
        index: null,
        id: null,
        payment: '',
        amount: '',
        paymentDate: '',
        status: 'pending',
        dateMenu: false,
        dragOver: false,
        paymentMethod: null,
        transactionId: null,
        transactionReceipt: null
      };
      this.showInstallmentDetailsDialog = false;
    },
    getScheduleStatusText(status) {
      const map = { upcoming: this.$t('finance.loanApplications.upcoming'), pending: this.$t('finance.loanApplications.pending'), paid: this.$t('finance.loanApplications.paid') };
      return map[status] || this.$t('finance.loanApplications.upcoming');
    },
    getScheduleStatusClass(status) {
      const map = { upcoming: 'status-upcoming', pending: 'status-pending', paid: 'status-paid' };
      return map[status] || 'status-upcoming';
    },
    triggerDisbursementFileInput() {
      this.$refs.disbursementFileInput?.click();
    },
    onDisbursementFileSelected(event) {
      const file = event.target.files?.[0];
      if (file) {
        this.$emit('update:disbursementFile', { name: file.name.length > 12 ? file.name.slice(0, 9) + '...' : file.name, file });
      }
      event.target.value = '';
    },
    onDisbursementDrop(event) {
      this.disbursementDragOver = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        this.$emit('update:disbursementFile', { name: file.name.length > 12 ? file.name.slice(0, 9) + '...' : file.name, file });
      }
    },
    downloadDisbursementFile() {
      if (this.disbursementFile?.file) {
        const url = URL.createObjectURL(this.disbursementFile.file);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.disbursementFile.file.name;
        a.click();
        URL.revokeObjectURL(url);
      }
    },
    downloadReceipt(fileURL) {
      if (fileURL) {
        const a = document.createElement('a');
        a.href = fileURL;
        a.download = this.getFilenameFromURL(fileURL);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    },
    removeReciept(source) {
      source.transactionReceipt = null;
    },
    removeDisbursementFile() {
      this.$emit('update:disbursementFile', null);
    },
    triggerRepaymentFileInput() {
      this.$refs.repaymentFileInput?.click();
    },
    onRepaymentFileSelected(event) {
      const file = event.target.files?.[0];
      if (file) {
        this.$emit('update:repaymentFile', { name: file.name.length > 12 ? file.name.slice(0, 9) + '...' : file.name, file });
      }
      event.target.value = '';
    },
    getFilenameFromURL(url) {
      console.log(url)
      const decoded = decodeURIComponent(url);
      return decoded.split('/').pop();
    },  
    onRepaymentDrop(event) {
      this.repaymentDragOver = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        this.$emit('update:repaymentFile', { name: file.name.length > 12 ? file.name.slice(0, 9) + '...' : file.name, file });
      }
    },
    downloadTemplate(templateName) {
      const templateFile = {
        repayment: 'repayment_schedule_template.xlsx',
        disbursement: 'disbursement_schedule_template.xlsx'
      };

      const fileName = templateFile[templateName];
      const fileUrl = `/files/${fileName}`;

      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    downloadRepaymentFile() {
      if (this.repaymentFile?.file) {
        const url = URL.createObjectURL(this.repaymentFile.file);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.repaymentFile.file.name;
        a.click();
        URL.revokeObjectURL(url);
      }
    },
    removeRepaymentFile() {
      this.$emit('update:repaymentFile', null);
    },
    onRecieptSelected(event, type) {
      if (type === 'payment') {
        this.$set(this.paymentDetailsForm, 'transactionReceipt', event.target.files?.[0]);
        this.$set(this.paymentDetailsForm, 'status', 'paid');
      } else {
        this.$set(this.installmentDetailsForm, 'transactionReceipt', event.target.files?.[0]);
        this.$set(this.installmentDetailsForm, 'status', 'paid');
      }
    },
    onRecieptDrop(event) {
      console.log(event);
    },
    triggerPaymentRecieptFileInput() {
      this.$refs.paymentRecieptFileInput?.click();
    },
    triggerInstallmentRecieptFileInput() {
      this.$refs.installmentRecieptFileInput?.click();
    },
    getDisbursementTransactionTableInfo(item) {
      if (!item.transactionId) {
        return "-"
      } else {
        return `${item.transactionId} \n ${item.paymentDate?.split('T')[0]}`
      }
    }
  },
};
</script>

<style scoped>
.view-label {
  font-size: 14px;
  color: #555;
}

.finalize-contract-desc {
  font-size: 13px;
  font-weight: 400;
  color: #333 !important;
}

.finalize-contract-subheading {
  font-size: 16px;
  font-weight: 700;
  color: #333 !important;
}

.loan-summary-box {
  background-color: #f5f5f5;
  border-radius: 4px;
}

.unit-combined .unit-input-col {
  flex: 1;
  min-width: 60px;
}

.unit-combined .unit-select-col {
  min-width: 145px;
  flex-shrink: 0;
}

.unit-combined .unit-select {
  min-width: 145px;
}

.date-field {
  height: 40px;
}
.date-field >>> .v-input__control {
  min-height: 40px !important;
}
.date-field >>> .v-input__slot {
  min-height: 40px !important;
}

.unit-combined .unit-select >>> .v-input__slot {
  border: none !important;
  background-color: #00bd73 !important;
  border-radius: 0 4px 4px 0 !important;
  padding-left: 12px !important;
  padding-right: 8px !important;
  min-width: 145px !important;
}

.unit-combined .rounded-r-0 >>> .v-input__slot {
  border-radius: 4px 0 0 4px !important;
}

.unit-combined .unit-select >>> .v-select__selection {
  color: #fff !important;
  font-weight: 500 !important;
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: unset !important;
  max-width: none !important;
}

.unit-combined .unit-select >>> .v-input__append-inner .v-icon {
  color: #fff !important;
}

.plan-section-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.plan-generate-btn {
  align-self: flex-start;
}

.dropZone {
  width: 100%;
  min-height: 120px;
  position: relative;
  border: 2px dashed #97a8b8;
  cursor: pointer;
}

.dropZone:hover {
  border: 2px solid #00bd73;
}

.dropZone-info {
  color: #a8a8a8;
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translate(0, -50%);
  text-align: center;
  font-size: 12px;
}

.dropZone-info .blue-color {
  color: #0057bd;
}

.dropZone-info .underline {
  text-decoration: underline;
}

.dropZone-info .gray-color {
  color: #9e9e9e;
}

.dropZone input {
  position: absolute;
  cursor: pointer;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
}

.dropZone-over {
  background: #5c5c5c;
  opacity: 0.8;
}

.dropZone--compact {
  min-height: 120px;
  width: 100%;
}

.dropZone--readonly {
  pointer-events: none;
  opacity: 0.85;
  cursor: default;
}

.attachment-icon-badge {
  font-weight: 600;
  font-size: 18px;
  letter-spacing: 0.5px;
  color: #135b00;
}

.attachment-download {
  position: absolute;
  top: 4px;
  left: 4px;
  right: auto;
  cursor: pointer;
}

.attachment-delete {
  position: absolute;
  top: 4px;
  right: 4px;
  bottom: auto;
  cursor: pointer;
}

.attachment-label {
  margin-top: 4px;
  font-size: 12px;
  color: #333;
}

/* Section title and Total Installments - same as table title (match Activity Log) */
.schedule-section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
}

/* Filter fields: 30% narrower (70% of md=4 width ~23.33%) at md and up */
@media (min-width: 960px) {
  .schedule-filter-field {
    max-width: 23.33%;
    flex: 0 0 auto;
  }
}

/* Pagination stays at the right end */
.schedule-pagination-col {
  margin-left: auto;
  flex: 0 0 auto;
}

/* Activity log table design - same as Details.vue Activity Log (primary green header) */
.activity-log-table >>> .v-data-table-header span {
  text-transform: uppercase !important;
  color: white !important;
  font-weight: bold !important;
  font-size: 12px !important;
  letter-spacing: 0.05em !important;
}

.activity-log-table >>> thead th {
  background-color: #117443 !important;
  color: white !important;
}

.activity-log-table >>> .v-data-table__wrapper tbody tr {
  cursor: pointer;
}

.activity-log-table >>> .v-data-table__wrapper tbody td {
  font-size: 14px !important;
  font-weight: 400 !important;
}

.activity-log-table >>> .status-pill-fixed {
  min-width: 180px !important;
  max-width: 180px !important;
  width: 180px !important;
  font-size: 14px !important;
}

.activity-log-table >>> .status-upcoming {
  background-color: #e0e0e0 !important;
  border: 2px solid #9e9e9e !important;
  color: #424242 !important;
}

.activity-log-table >>> .status-pending {
  background-color: #fff8e1 !important;
  border: 2px solid #ffc107 !important;
  color: #f57f17 !important;
}

.activity-log-table >>> .status-paid {
  background-color: #c1ffe7 !important;
  border: 2px solid #00bd73 !important;
  color: #489e16 !important;
}

/* Payment Details dialog */
.payment-details-dialog-title {
  font-weight: bold;
  font-size: 1rem;
}

.payment-details-heading {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
}

.payment-details-close-btn {
  text-transform: uppercase;
  font-weight: 600;
}

/* Installment Details dialog */
.installment-details-dialog-title {
  font-weight: bold;
  font-size: 1rem;
}

.installment-details-heading {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
}

.installment-details-close-btn {
  text-transform: uppercase;
  font-weight: 600;
}

.finalize-contract-text {
  color: #333 !important;
}

.finalize-contract-desc {
  font-size: 13px;
  font-weight: 400;
  color: #333 !important;
}

.finalize-contract-subheading {
  font-size: 16px;
  font-weight: 700;
  color: #333 !important;
}

.finalize-contract-radio-group {
  flex-wrap: wrap;
}

.finalize-contract-radio-group >>> .v-radio {
  margin-right: 24px;
}

.finalize-contract-radio-group >>> .v-input--radio-group__input {
  flex-direction: row;
}

.finalize-contract-radio-group >>> .v-radio .v-label {
  font-size: 13px !important;
  font-weight: 400 !important;
  color: #333 !important;
}

.view-label {
  font-size: 14px;
  color: #555;
}

.attachment-card {
  height: 115px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 20px;
}

.attachment-icon {
  width: 90px;
  height: 90px;
  background: #e5f8f1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.attachment-icon-badge {
  font-weight: 600;
  font-size: 18px;
  letter-spacing: 0.5px;
  color: #135b00;
}

.attachment-download {
  position: absolute;
  top: 4px;
  left: 4px;
  right: auto;
  cursor: pointer;
}

.attachment-delete {
  position: absolute;
  top: 4px;
  right: 4px;
  bottom: auto;
  cursor: pointer;
}

.dropZone {
  width: 100%;
  min-height: 120px;
  position: relative;
  border: 2px dashed #97a8b8;
  cursor: pointer;
}

.dropZone:hover {
  border: 2px solid #00bd73;
}

.dropZone-info {
  color: #a8a8a8;
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translate(0, -50%);
  text-align: center;
  font-size: 12px;
}

.dropZone-info .blue-color {
  color: #0057bd;
}

.dropZone-info .underline {
  text-decoration: underline;
}

.dropZone-info .gray-color {
  color: #9e9e9e;
}

.dropZone input {
  position: absolute;
  cursor: pointer;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
}

.dropZone-over {
  background: #5c5c5c;
  opacity: 0.8;
}

.attachment-label {
  margin-top: 4px;
  font-size: 12px;
  color: #333;
}
</style>
